"use client";

import { c as _c } from "react/compiler-runtime";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import SecondaryButton from "@/components/utils/button/SecondaryButton";
import { motion } from "framer-motion";
import { useMemo, type FC } from "react";
import AdditionalMediaUpload from "../components/AdditionalMediaUpload";
import AirdropConfig from "../components/AirdropConfig";
import AllowlistBatchManager, { type AllowlistBatchRaw } from "../components/AllowlistBatchManager";
import PaymentConfig from "../components/PaymentConfig";
import { AIRDROP_TOTAL, type AirdropEntry, type PaymentInfo } from "../types/OperationalData";
import type { TraitsData } from "../types/TraitsData";
import { validateStrictAddress } from "../utils/addressValidation";
import { METADATA_VALUE_MAX_LENGTH, getSubmissionMetadataLengthValidation, type MetadataValueLengthStatus } from "../utils/submissionMetadata";
import { validateTokenIdFormat } from "../utils/tokenParsing";
interface AdditionalInfoStepProps {
  readonly traits: TraitsData;
  readonly airdropEntries: AirdropEntry[];
  readonly onAirdropEntriesChange: (entries: AirdropEntry[]) => void;
  readonly paymentInfo: PaymentInfo;
  readonly onPaymentInfoChange: (paymentInfo: PaymentInfo) => void;
  readonly allowlistBatches: AllowlistBatchRaw[];
  readonly supportingMedia: string[];
  readonly artworkCommentary: string;
  readonly aboutArtist: string;
  readonly previewImage: string;
  readonly promoVideo: string;
  readonly requiresPreviewImage: boolean;
  readonly requiresPromoVideoOption: boolean;
  readonly previewRequiredMediaType: string | null;
  readonly onBatchesChange: (batches: AllowlistBatchRaw[]) => void;
  readonly onSupportingMediaChange: (media: string[]) => void;
  readonly onPreviewImageChange: (url: string) => void;
  readonly onPromoVideoChange: (url: string) => void;
  readonly onArtworkCommentaryChange: (commentary: string) => void;
  readonly onAboutArtistChange: (aboutArtist: string) => void;
  readonly onBack: () => void;
  readonly onPreview: () => void;
  readonly onSubmit: () => void;
  readonly isSubmitting: boolean;
}
const AdditionalInfoStep: FC<AdditionalInfoStepProps> = t0 => {
  const $ = _c(92);
  const {
    traits,
    airdropEntries,
    onAirdropEntriesChange,
    paymentInfo,
    onPaymentInfoChange,
    allowlistBatches,
    supportingMedia,
    artworkCommentary,
    aboutArtist,
    previewImage,
    promoVideo,
    requiresPreviewImage,
    requiresPromoVideoOption,
    previewRequiredMediaType,
    onBatchesChange,
    onSupportingMediaChange,
    onPreviewImageChange,
    onPromoVideoChange,
    onArtworkCommentaryChange,
    onAboutArtistChange,
    onBack,
    onPreview,
    onSubmit,
    isSubmitting
  } = t0;
  let t1;
  if ($[0] !== aboutArtist || $[1] !== airdropEntries || $[2] !== allowlistBatches || $[3] !== artworkCommentary || $[4] !== paymentInfo || $[5] !== previewImage || $[6] !== promoVideo || $[7] !== supportingMedia || $[8] !== traits) {
    t1 = getSubmissionMetadataLengthValidation({
      traits,
      operationalData: {
        airdrop_config: airdropEntries,
        payment_info: paymentInfo,
        allowlist_batches: allowlistBatches,
        additional_media: {
          artist_profile_media: [],
          artwork_commentary_media: supportingMedia,
          preview_image: previewImage,
          promo_video: promoVideo
        },
        commentary: artworkCommentary,
        about_artist: aboutArtist
      }
    });
    $[0] = aboutArtist;
    $[1] = airdropEntries;
    $[2] = allowlistBatches;
    $[3] = artworkCommentary;
    $[4] = paymentInfo;
    $[5] = previewImage;
    $[6] = promoVideo;
    $[7] = supportingMedia;
    $[8] = traits;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const metadataLengthValidation = t1;
  const metadataStatuses = metadataLengthValidation.statusesByKey;
  const aboutArtistStatus = metadataStatuses.about_artist;
  const commentaryStatus = metadataStatuses.commentary;
  const metadataLengthError = _temp;
  let t2;
  if ($[10] !== aboutArtistStatus) {
    t2 = metadataLengthError(aboutArtistStatus);
    $[10] = aboutArtistStatus;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const aboutArtistError = t2;
  let t3;
  if ($[12] !== commentaryStatus) {
    t3 = metadataLengthError(commentaryStatus);
    $[12] = commentaryStatus;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const artworkCommentaryError = t3;
  const getContractError = _temp2;
  const getTokenIdsError = _temp3;
  let t4;
  if ($[14] !== aboutArtist || $[15] !== airdropEntries || $[16] !== allowlistBatches || $[17] !== artworkCommentary || $[18] !== metadataLengthValidation.hasErrors || $[19] !== paymentInfo || $[20] !== previewImage || $[21] !== requiresPreviewImage) {
    t4 = () => {
      const hasInvalidCount = airdropEntries.some(_temp4);
      if (hasInvalidCount) {
        return false;
      }
      const totalAllocated = airdropEntries.reduce(_temp5, 0);
      if (totalAllocated !== AIRDROP_TOTAL) {
        return false;
      }
      const hasEntryWithCountButNoValidAddress = airdropEntries.some(_temp6);
      if (hasEntryWithCountButNoValidAddress) {
        return false;
      }
      const hasEntryWithAddressButNoCount = airdropEntries.some(_temp7);
      if (hasEntryWithAddressButNoCount) {
        return false;
      }
      const hasInvalidAddress = airdropEntries.some(_temp8);
      if (hasInvalidAddress) {
        return false;
      }
      if (!paymentInfo.payment_address || !validateStrictAddress(paymentInfo.payment_address)) {
        return false;
      }
      const hasInvalidBatch = allowlistBatches.some(_temp9);
      if (hasInvalidBatch) {
        return false;
      }
      const hasInvalidTokenIds = allowlistBatches.some(_temp10);
      if (hasInvalidTokenIds) {
        return false;
      }
      if (!artworkCommentary.trim()) {
        return false;
      }
      if (!aboutArtist.trim()) {
        return false;
      }
      if (requiresPreviewImage && !previewImage) {
        return false;
      }
      if (metadataLengthValidation.hasErrors) {
        return false;
      }
      return true;
    };
    $[14] = aboutArtist;
    $[15] = airdropEntries;
    $[16] = allowlistBatches;
    $[17] = artworkCommentary;
    $[18] = metadataLengthValidation.hasErrors;
    $[19] = paymentInfo;
    $[20] = previewImage;
    $[21] = requiresPreviewImage;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const isFormValid = t4;
  const formValid = isFormValid();
  let t5;
  let t6;
  let t7;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      opacity: 0,
      y: 20
    };
    t6 = {
      opacity: 1,
      y: 0
    };
    t7 = {
      opacity: 0,
      y: -20
    };
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t5 = $[23];
    t6 = $[24];
    t7 = $[25];
  }
  let t8;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="tw-mb-8 tw-text-sm tw-text-iron-400">Complete the following details for distribution and storytelling purposes.</p>;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== airdropEntries || $[28] !== metadataStatuses.airdrop_config || $[29] !== onAirdropEntriesChange) {
    t9 = <AirdropConfig entries={airdropEntries} onEntriesChange={onAirdropEntriesChange} airdropLengthStatus={metadataStatuses.airdrop_config} />;
    $[27] = airdropEntries;
    $[28] = metadataStatuses.airdrop_config;
    $[29] = onAirdropEntriesChange;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== metadataStatuses.payment_info || $[32] !== onPaymentInfoChange || $[33] !== paymentInfo) {
    t10 = <PaymentConfig paymentInfo={paymentInfo} onPaymentInfoChange={onPaymentInfoChange} paymentInfoLengthStatus={metadataStatuses.payment_info} />;
    $[31] = metadataStatuses.payment_info;
    $[32] = onPaymentInfoChange;
    $[33] = paymentInfo;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const t11 = metadataStatuses.allowlist_batches;
  let t12;
  if ($[35] !== allowlistBatches) {
    let t13;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = batch_1 => {
        const contractError = getContractError(batch_1.contract);
        const tokenIdsError = getTokenIdsError(batch_1.token_ids_raw);
        return {
          ...(contractError ? {
            contract: contractError
          } : {}),
          ...(tokenIdsError ? {
            token_ids: tokenIdsError
          } : {})
        };
      };
      $[37] = t13;
    } else {
      t13 = $[37];
    }
    t12 = allowlistBatches.map(t13);
    $[35] = allowlistBatches;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[38] !== allowlistBatches || $[39] !== metadataStatuses.allowlist_batches || $[40] !== onBatchesChange || $[41] !== t12) {
    t13 = <AllowlistBatchManager batches={allowlistBatches} onBatchesChange={onBatchesChange} allowlistLengthStatus={t11} errors={t12} />;
    $[38] = allowlistBatches;
    $[39] = metadataStatuses.allowlist_batches;
    $[40] = onBatchesChange;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  const t14 = aboutArtistStatus?.isWarning && !aboutArtistStatus.isError ? aboutArtistStatus : undefined;
  const t15 = commentaryStatus?.isWarning && !commentaryStatus.isError ? commentaryStatus : undefined;
  let t16;
  if ($[43] !== aboutArtistError) {
    t16 = aboutArtistError ? {
      aboutArtist: aboutArtistError
    } : {};
    $[43] = aboutArtistError;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== artworkCommentaryError) {
    t17 = artworkCommentaryError ? {
      artworkCommentary: artworkCommentaryError
    } : {};
    $[45] = artworkCommentaryError;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t16 || $[48] !== t17) {
    t18 = {
      ...t16,
      ...t17
    };
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== aboutArtist || $[51] !== artworkCommentary || $[52] !== metadataStatuses.additional_media || $[53] !== onAboutArtistChange || $[54] !== onArtworkCommentaryChange || $[55] !== onPreviewImageChange || $[56] !== onPromoVideoChange || $[57] !== onSupportingMediaChange || $[58] !== previewImage || $[59] !== previewRequiredMediaType || $[60] !== promoVideo || $[61] !== requiresPreviewImage || $[62] !== requiresPromoVideoOption || $[63] !== supportingMedia || $[64] !== t14 || $[65] !== t15 || $[66] !== t18) {
    t19 = <AdditionalMediaUpload supportingMedia={supportingMedia} artworkCommentary={artworkCommentary} aboutArtist={aboutArtist} previewImage={previewImage} promoVideo={promoVideo} requiresPreviewImage={requiresPreviewImage} requiresPromoVideoOption={requiresPromoVideoOption} previewRequiredMediaType={previewRequiredMediaType} onSupportingMediaChange={onSupportingMediaChange} onPreviewImageChange={onPreviewImageChange} onPromoVideoChange={onPromoVideoChange} onArtworkCommentaryChange={onArtworkCommentaryChange} onAboutArtistChange={onAboutArtistChange} aboutArtistLengthStatus={t14} artworkCommentaryLengthStatus={t15} additionalMediaLengthStatus={metadataStatuses.additional_media} errors={t18} />;
    $[50] = aboutArtist;
    $[51] = artworkCommentary;
    $[52] = metadataStatuses.additional_media;
    $[53] = onAboutArtistChange;
    $[54] = onArtworkCommentaryChange;
    $[55] = onPreviewImageChange;
    $[56] = onPromoVideoChange;
    $[57] = onSupportingMediaChange;
    $[58] = previewImage;
    $[59] = previewRequiredMediaType;
    $[60] = promoVideo;
    $[61] = requiresPreviewImage;
    $[62] = requiresPromoVideoOption;
    $[63] = supportingMedia;
    $[64] = t14;
    $[65] = t15;
    $[66] = t18;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== t10 || $[69] !== t13 || $[70] !== t19 || $[71] !== t9) {
    t20 = <div className="tw-mx-auto tw-w-full tw-max-w-4xl tw-flex-1 tw-overflow-y-auto tw-px-4 tw-pb-8 tw-pt-8 tw-scrollbar-thin tw-scrollbar-track-iron-900 tw-scrollbar-thumb-iron-700 md:tw-px-8">{t8}<div className="tw-flex tw-flex-col tw-gap-y-10">{t9}{t10}{t13}{t19}</div></div>;
    $[68] = t10;
    $[69] = t13;
    $[70] = t19;
    $[71] = t9;
    $[72] = t20;
  } else {
    t20 = $[72];
  }
  let t21;
  if ($[73] !== isSubmitting || $[74] !== onBack) {
    t21 = <SecondaryButton onClicked={onBack} disabled={isSubmitting}>Back</SecondaryButton>;
    $[73] = isSubmitting;
    $[74] = onBack;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  const t22 = !formValid || isSubmitting;
  let t23;
  if ($[76] !== onPreview || $[77] !== t22) {
    t23 = <SecondaryButton onClicked={onPreview} disabled={t22}>Preview</SecondaryButton>;
    $[76] = onPreview;
    $[77] = t22;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  const t24 = !formValid;
  let t25;
  if ($[79] !== isSubmitting || $[80] !== onSubmit || $[81] !== t24) {
    t25 = <PrimaryButton onClicked={onSubmit} disabled={t24} loading={isSubmitting} padding="tw-px-6 tw-py-3">Submit Artwork</PrimaryButton>;
    $[79] = isSubmitting;
    $[80] = onSubmit;
    $[81] = t24;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  let t26;
  if ($[83] !== t23 || $[84] !== t25) {
    t26 = <div className="tw-flex tw-items-center tw-gap-2">{t23}{t25}</div>;
    $[83] = t23;
    $[84] = t25;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  let t27;
  if ($[86] !== t21 || $[87] !== t26) {
    t27 = <div className="tw-mt-auto tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-py-3"><div className="tw-mx-auto tw-flex tw-w-full tw-max-w-4xl tw-items-center tw-justify-between tw-px-4 md:tw-px-8">{t21}{t26}</div></div>;
    $[86] = t21;
    $[87] = t26;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  let t28;
  if ($[89] !== t20 || $[90] !== t27) {
    t28 = <motion.div initial={t5} animate={t6} exit={t7} className="tw-flex tw-h-full tw-w-full tw-flex-col">{t20}{t27}</motion.div>;
    $[89] = t20;
    $[90] = t27;
    $[91] = t28;
  } else {
    t28 = $[91];
  }
  return t28;
};
export default AdditionalInfoStep;
function _temp(status) {
  if (!status?.isError) {
    return;
  }
  return `${status.length}/${METADATA_VALUE_MAX_LENGTH} characters exceeds limit`;
}
function _temp2(address) {
  if (!address) {
    return;
  }
  if (!validateStrictAddress(address)) {
    return "Invalid contract address";
  }
}
function _temp3(tokenIds) {
  return validateTokenIdFormat(tokenIds) ?? undefined;
}
function _temp4(e) {
  return !Number.isFinite(e.count) || e.count < 0 || !Number.isInteger(e.count);
}
function _temp5(sum, e_0) {
  return sum + e_0.count;
}
function _temp6(e_1) {
  return e_1.count > 0 && (!e_1.address || !validateStrictAddress(e_1.address));
}
function _temp7(e_2) {
  return e_2.address.length > 0 && validateStrictAddress(e_2.address) && e_2.count <= 0;
}
function _temp8(e_3) {
  return e_3.address.length > 0 && !validateStrictAddress(e_3.address);
}
function _temp9(batch) {
  return !batch.contract || !validateStrictAddress(batch.contract);
}
function _temp10(batch_0) {
  return validateTokenIdFormat(batch_0.token_ids_raw) !== null;
}